/**
 * 单店营收信息页面
 *
 * 针对单个门店的运行信息：
 * - 左侧：组件区（横向排列、自动换行）；门店选择在顶栏左侧（替代默认标题）
 * - 右侧：AI 工具栏（Splitter 分割，可向右收拢）
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { CloudSyncOutlined, ReloadOutlined, SortAscendingOutlined } from "@ant-design/icons";
import { Button, Empty, Select, Splitter, message } from "antd";
import { CompsAi, type CompsAiNewDesignRef } from "../../compsAi";
import { useMsOrgAccessibleHotelOptions } from "../../hooks/useMsOrgAccessibleHotelOptions";
import { StoreDataLoader } from "@ms/share";
import { useAiAssistant, getComponentLabel, getComponentTimeDirection, ALL_COMPONENT_LIST } from "../store-revenue-monitor/hooks/useAiAssistant";
import { createThinkingModes } from "../store-revenue-monitor/configs/panelAnalysisConfig";
import { convertStoreComponentClickToStandard, createStandardAiMessage } from "../store-revenue-monitor/utils/aiMessageConverter";
import { DEFAULT_COMPONENT_ORDER } from "../store-revenue-monitor/configs/componentOrder";
import { buildComponentDataPackage } from "../store-revenue-monitor/utils/buildComponentDataPackage";
import { getBusinessReportDailyWithCache } from "../store-revenue-monitor/utils/businessReportDailyCache";
import { buildOperationAnalysisFromList } from "../store-revenue-monitor/utils/operationAnalysisFromBusinessReport";
import { getRevparChartWithCache } from "../store-revenue-monitor/components/RevPAR/utils/revparCache";
import { getRoomStatusRecentlyWithCache } from "../store-revenue-monitor/utils/roomStatusRecentlyCache";
import { getComponentDataByTimeRanges } from "../../service/storeRevenueApi";
import { useLayoutHeaderLeft, useLayoutHeaderRight } from "../../components/LayoutShellContext";
import "./store-revenue-single.css";
import { useHisLayoutDebugSampling } from "../../hooks/useHisLayoutDebugSampling";
import { shouldShowDebugFloat } from "../../utils/debugFloatEnabled";
import { pushHisDebugLog } from "../../utils/hisDebugLog";

const STORE_REVENUE_SINGLE_ORDER_KEY = "store-revenue-single:component-order";

/** 出租率等运营分析：后端 store-data-from-cache 返回空结构，需用前端 businessReport 补数据 */
const OPERATION_ANALYSIS_TYPES = [
  "occupancy-analysis",
  "room-health",
  "customer-source",
  "discount-analysis",
  "channel-analysis",
  "channel-info",
  "operation-efficiency",
] as const;

/** 单店营收信息页 */
const StoreRevenueSingle: React.FC = () => {
  const [selectedStoreId, setSelectedStoreId] = useState<number | undefined>();
  const [selectedStoreName, setSelectedStoreName] = useState<string>("");

  const [componentOrder, setComponentOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(STORE_REVENUE_SINGLE_ORDER_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed) && parsed.every((v) => typeof v === "string")) {
          return parsed;
        }
      }
    } catch {
      /* ignore */
    }
    return [...DEFAULT_COMPONENT_ORDER];
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        STORE_REVENUE_SINGLE_ORDER_KEY,
        JSON.stringify(componentOrder)
      );
    } catch {
      /* ignore */
    }
  }, [componentOrder]);

  const handleResetComponentOrder = useCallback(() => {
    setComponentOrder([...DEFAULT_COMPONENT_ORDER]);
    message.success("已恢复默认顺序");
  }, []);

  const { clearAll } = useAiAssistant(undefined, undefined, { skipPageConfig: true });
  const compsAiRef = useRef<CompsAiNewDesignRef | null>(null);

  /** AI 当前关注门店（左侧选中即为当前门店） */
  const currentStoreIdForAiRef = useRef<string>("");
  const [currentStoreNameForAi, setCurrentStoreNameForAi] = useState<string>("");
  /** 缓存：StoreDataLoader 数据就绪时写入，供右侧组件列表「xxx 分析」直接使用 */
  const storeDataCacheRef = useRef<Record<string, { storeId: number; storeName: string; storeData: any }>>({});
  const lastStoreDataRef = useRef<{ storeId: number; storeName: string; storeData: any } | null>(null);

  // 初始状态（未选中酒店）时清空 AI 助手内容，保持传入的信息、思考方式、反馈结果为空
  useEffect(() => {
    if (!selectedStoreId) {
      clearAll();
      compsAiRef.current?.clearSelection?.();
      currentStoreIdForAiRef.current = "";
      setCurrentStoreNameForAi("");
    }
  }, [selectedStoreId, clearAll]);

  const handleQuickCommand = useCallback(
    (text: string) => {
      try {
        const standardMessage = createStandardAiMessage({
          componentId: "quick-command",
          // 单店页复用「门店营收监控」的 AI 配置（提示词模板等），保持一致口径
          pageId: "store-revenue-monitor",
          payload: { userInput: text.trim(), type: "quickCommand" },
        });
        compsAiRef.current?.receiveMessage(standardMessage)?.catch(() => {});
      } catch (e) {
        console.error("快捷指令发送失败：", e);
      }
    },
    []
  );

  const handleComponentTitleClick = useCallback(
    (params: {
      storeId: number;
      storeName?: string;
      componentType: string;
      componentData: any;
    }) => {
      try {
        // 点击组件标题时，也将该门店设为 AI 当前关注门店
        currentStoreIdForAiRef.current = String(params.storeId);
        setCurrentStoreNameForAi(params.storeName ?? `门店 ${params.storeId}`);
        const standardMessage = convertStoreComponentClickToStandard(
          {
            storeId: String(params.storeId),
            componentType: params.componentType as any,
            data: params.componentData,
            metadata: {
              label: getComponentLabel(params.componentType),
              storeName: params.storeName,
            },
            clearPrevious: true,
          } as any,
          // 复用「门店营收监控」的 AI 配置（提示词模板等）
          "store-revenue-monitor",
        );
        compsAiRef.current?.receiveMessage(standardMessage)?.catch(() => {});
      } catch (e) {
        console.error("发送到AI失败：", e);
      }
    },
    [],
  );

  /** 右侧组件列表「xxx 分析」：直接用当前选中门店数据组包发送给 AI（与门店营收监控一致的补拉策略） */
  const handleComponentAnalyze = useCallback(async (componentType: string) => {
    const storeIdStr = currentStoreIdForAiRef.current || (selectedStoreId != null ? String(selectedStoreId) : "");
    const cached = storeIdStr ? storeDataCacheRef.current[storeIdStr] : undefined;
    const last = lastStoreDataRef.current;
    const base = last?.storeData ? last : cached;
    if (!base?.storeData) {
      message.warning("请先在左侧选择门店");
      return;
    }
    const storeId = base.storeId;
    const storeName = base.storeName;
    const storeData = base.storeData;

    try {
      let dataToUse = storeData;
      if (OPERATION_ANALYSIS_TYPES.includes(componentType as any)) {
        const fieldMap: Record<string, string> = {
          "occupancy-analysis": "occupancyAnalysis",
          "room-health": "roomHealth",
          "customer-source": "customerSource",
          "discount-analysis": "discountAnalysis",
          "channel-analysis": "channelAnalysis",
          "channel-info": "channelInfo",
          "operation-efficiency": "operationEfficiency",
        };
        const dataKey = fieldMap[componentType];
        const storeDataKey = componentType === "channel-info" ? "channelInfoChart" : dataKey;
        const currentSlice = storeData?.[storeDataKey];
        const isEmpty =
          currentSlice == null ||
          (currentSlice?.chartData != null &&
            (!Array.isArray((currentSlice.chartData as any)?.categories) ||
              (currentSlice.chartData as any).categories.length === 0));
        if (isEmpty) {
          try {
            const list = await getBusinessReportDailyWithCache(storeId);
            const built = buildOperationAnalysisFromList(list, "日度") as any;
            const value = componentType === "channel-info" ? built.channelInfo : built[dataKey];
            dataToUse = { ...storeData, [storeDataKey]: value };
          } catch {
            // ignore，继续用原 storeData
          }
        }
      } else if (componentType === "revpar") {
        const rev = storeData?.revPARChart?.chartData;
        const revEmpty =
          rev == null ||
          !Array.isArray((rev as any)?.categories) ||
          (rev as any).categories.length === 0;
        if (revEmpty) {
          try {
            const revparData = await getRevparChartWithCache(storeId);
            dataToUse = { ...storeData, revPARChart: revparData };
          } catch {
            // ignore
          }
        }
      } else if (componentType === "room-status-recently") {
        const baseChart = storeData?.roomStatusRecently?.baseChartData;
        const recentEmpty =
          baseChart == null ||
          !Array.isArray((baseChart as any)?.categories) ||
          (baseChart as any).categories.length === 0;
        if (recentEmpty) {
          try {
            const baseChartData = await getRoomStatusRecentlyWithCache(storeId);
            dataToUse = { ...storeData, roomStatusRecently: { baseChartData } };
          } catch {
            // ignore
          }
        }
      }

      const timeDirection = getComponentTimeDirection(componentType);
      // 对“基础组件”（房态/订单流速/渠道价等）：优先走后端 getComponentDataByTimeRanges，确保与组件标题点击一致且不易出现空数据；
      // 对运营分析/RevPAR/远期房态等：仍走补拉 + 本地打包（后端 cache 不返/直调 PMS）。
      let componentData: any;
      const shouldPreferTimeRangesApi =
        !OPERATION_ANALYSIS_TYPES.includes(componentType as any) &&
        componentType !== "revpar" &&
        componentType !== "room-status-recently";
      if (shouldPreferTimeRangesApi) {
        try {
          const pkg = await getComponentDataByTimeRanges(storeId, componentType, timeDirection);
          // 实时房态：用页面已加载的 current（与页面展示一致）
          if (componentType === "room-status" && dataToUse?.roomStatus?.dataSource?.length) {
            pkg.current = dataToUse.roomStatus;
          }
          componentData = pkg;
        } catch {
          // 降级为本地打包
          componentData = buildComponentDataPackage(storeId, componentType, dataToUse, timeDirection);
        }
      } else {
        componentData = buildComponentDataPackage(storeId, componentType, dataToUse, timeDirection);
      }
      const standardMessage = convertStoreComponentClickToStandard(
        {
          storeId: String(storeId),
          componentType: componentType as any,
          data: componentData,
          metadata: { label: getComponentLabel(componentType), storeName },
          clearPrevious: true,
        } as any,
        // 复用「门店营收监控」的 AI 配置（提示词模板等）
        "store-revenue-monitor",
      );
      compsAiRef.current?.receiveMessage(standardMessage)?.catch(() => {});
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("单店列表分析发送到AI失败：", e);
    }
  }, [selectedStoreId]);

  const {
    hotelOptions,
    loading: hotelOptionsLoading,
    loadError: hotelOptionsError,
    allowedIds,
    refetchAccessibleStores,
  } = useMsOrgAccessibleHotelOptions();

  const refreshStoreDataRef = useRef<(() => void) | null>(null);
  const leftScrollDebugRef = useRef<HTMLDivElement>(null);
  const storeContentDebugRef = useRef<HTMLDivElement>(null);
  useHisLayoutDebugSampling(shouldShowDebugFloat(), leftScrollDebugRef, storeContentDebugRef);

  const prevSelectedStoreIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (selectedStoreId == null) {
      if (prevSelectedStoreIdRef.current != null) {
        pushHisDebugLog("store:cleared selection");
      }
    } else {
      pushHisDebugLog(`store:selected id=${selectedStoreId}`);
    }
    prevSelectedStoreIdRef.current = selectedStoreId;
  }, [selectedStoreId]);

  const setHeaderRight = useLayoutHeaderRight();
  const setHeaderLeft = useLayoutHeaderLeft();

  useEffect(() => {
    if (hotelOptionsError) {
      message.error(hotelOptionsError);
    }
  }, [hotelOptionsError]);

  /** msOrg 授权列表加载完成后，若当前选中门店不在授权范围内则清空 */
  useEffect(() => {
    if (hotelOptionsLoading || allowedIds === null) return;
    const allow = new Set(allowedIds);
    if (selectedStoreId != null && !allow.has(selectedStoreId)) {
      setSelectedStoreId(undefined);
      setSelectedStoreName("");
      currentStoreIdForAiRef.current = "";
      setCurrentStoreNameForAi("");
    }
  }, [hotelOptionsLoading, allowedIds, selectedStoreId]);

  const handleStoreSelect = useCallback(
    (value: number | undefined, option: { label?: string } | undefined) => {
      if (value == null) {
        setSelectedStoreId(undefined);
        setSelectedStoreName("");
        currentStoreIdForAiRef.current = "";
        setCurrentStoreNameForAi("");
        return;
      }
      setSelectedStoreId(value);
      const name = option?.label ?? `门店 ${value}`;
      setSelectedStoreName(name);
      // 左侧选中门店即为 AI 当前关注门店
      currentStoreIdForAiRef.current = String(value);
      setCurrentStoreNameForAi(name);
    },
    []
  );

  useLayoutEffect(() => {
    setHeaderLeft(
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        <span style={{ flexShrink: 0, fontSize: 14, color: "rgba(0,0,0,0.88)" }}>
          门店：
        </span>
        <Select
          allowClear
          showSearch
          loading={hotelOptionsLoading}
          placeholder="请选择门店"
          options={hotelOptions}
          optionFilterProp="label"
          style={{ minWidth: 280, maxWidth: 420 }}
          value={selectedStoreId ?? undefined}
          onChange={(v, opt) =>
            handleStoreSelect(
              v as number | undefined,
              Array.isArray(opt) ? undefined : (opt as { label?: string } | undefined)
            )
          }
        />
      </div>
    );
    return () => setHeaderLeft(null);
  }, [
    setHeaderLeft,
    hotelOptions,
    hotelOptionsLoading,
    selectedStoreId,
    handleStoreSelect,
  ]);

  useLayoutEffect(() => {
    setHeaderRight(
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="default"
          icon={<CloudSyncOutlined />}
          style={{ height: 32 }}
          loading={hotelOptionsLoading}
          onClick={() => refetchAccessibleStores()}
        >
          同步门店
        </Button>
        <Button
          type="default"
          icon={<SortAscendingOutlined />}
          style={{ height: 32 }}
          onClick={handleResetComponentOrder}
        >
          重排顺序
        </Button>
        {typeof selectedStoreId === "number" ? (
          <Button
            type="default"
            icon={<ReloadOutlined />}
            style={{ height: 32 }}
            onClick={() => refreshStoreDataRef.current?.()}
          >
            刷新数据
          </Button>
        ) : null}
      </div>
    );
    return () => setHeaderRight(null);
  }, [
    setHeaderRight,
    hotelOptionsLoading,
    selectedStoreId,
    refetchAccessibleStores,
    handleResetComponentOrder,
  ]);

  const displayName =
    selectedStoreName?.trim() || (selectedStoreId ? `门店${selectedStoreId}` : "");

  let emptyStoreDescription = "请在顶栏选择门店";
  if (hotelOptionsLoading) {
    emptyStoreDescription = "正在加载可访问门店…";
  } else if (hotelOptions.length === 0) {
    emptyStoreDescription =
      "当前账号在 msOrg 中无可访问门店，或门店未同步到中台缓存";
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0%",
        minHeight: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Splitter
        className="store-revenue-single-splitter"
        style={{
          flex: "1 1 0%",
          flexBasis: 0,
          minHeight: 0,
          height: 0,
          minWidth: 0,
          width: "100%",
        }}
      >
        {/* 左侧：组件区；Panel 必须 minHeight:0，否则 wrapLayout 内容会把整页撑高 */}
        <Splitter.Panel
          min="30%"
          style={{
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              flex: "1 1 0%",
              flexBasis: 0,
              minHeight: 0,
              height: 0,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              ref={leftScrollDebugRef}
              data-his-layout="store-single-left-scroll"
              style={{
                flex: "1 1 0%",
                minHeight: 0,
                minWidth: 0,
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {selectedStoreId == null ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <Empty description={emptyStoreDescription} />
                </div>
              ) : (
                <div
                  ref={storeContentDebugRef}
                  data-his-layout="store-single-content"
                  style={{ minHeight: 0, width: "100%" }}
                >
                  <StoreDataLoader
                    storeId={selectedStoreId}
                    storeName={displayName}
                    refreshKey={0}
                    onTitleClick={() => {}}
                    onRefresh={() => {}}
                    onComponentTitleClick={handleComponentTitleClick}
                    onStoreDataReady={(storeData) => {
                      const entry = { storeId: selectedStoreId, storeName: displayName, storeData };
                      storeDataCacheRef.current[String(selectedStoreId)] = entry;
                      lastStoreDataRef.current = entry;
                    }}
                    bareLayout
                    wrapLayout
                    draggable
                    componentOrder={componentOrder}
                    onComponentOrderChange={setComponentOrder}
                    onRefreshReady={(refresh) => {
                      refreshStoreDataRef.current = refresh;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </Splitter.Panel>

        {/* 右侧：AI 工具栏（可向右收拢） */}
        <Splitter.Panel
          defaultSize={300}
          min="260px"
          collapsible
          style={{
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              flex: "1 1 0%",
              flexBasis: 0,
              minHeight: 0,
              height: 0,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <CompsAi
              ref={compsAiRef as any}
              pageId="store-revenue-monitor"
              microServiceId="msCentral"
              apiBaseUrl={undefined}
              onClearAll={clearAll}
              height="100%"
              skipPageConfigLoad
              thinkingModes={createThinkingModes()}
              componentList={ALL_COMPONENT_LIST}
              onComponentAnalyze={handleComponentAnalyze}
              currentStoreName={currentStoreNameForAi || selectedStoreName}
              currentStoreId={selectedStoreId}
              onQuickCommand={handleQuickCommand}
              disabled={!selectedStoreId}
            />
          </div>
        </Splitter.Panel>
      </Splitter>
    </div>
  );
};

export default StoreRevenueSingle;
